import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, Clock, CalendarDays, CheckCircle2, ListChecks } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FORMULES = {
  'appartement-1': 'Appt 1er',
  'appartement-2': 'Appt 2eme',
  'gite-seul': 'Gite seul',
  'gite-salle': 'Gite + Salle',
};

function TaskRow({ task, onToggle }) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${task.completed ? 'bg-[#F5F5F0]/50' : 'bg-white'}`} data-testid={`overview-task-${task.id}`}>
      <Checkbox
        checked={task.completed}
        onCheckedChange={() => onToggle(task.id)}
        className="border-[#C4A962] data-[state=checked]:bg-[#8A9A5B] data-[state=checked]:border-[#8A9A5B]"
        data-testid={`overview-check-${task.id}`}
      />
      <div className="flex-1 min-w-0">
        <span className={`text-sm ${task.completed ? 'line-through text-[#595959]' : 'text-[#2C3E30]'}`}>{task.title}</span>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-[#C4A962] font-medium">{task.res_name}</span>
          {task.res_accommodation && <span className="text-[10px] text-[#595959]">{FORMULES[task.res_accommodation] || task.res_accommodation}</span>}
          {task.due_label && <Badge className="text-[8px] bg-[#F5F5F0] text-[#595959] border-[#E5E5E0] border py-0 h-4">{task.due_label}</Badge>}
        </div>
      </div>
      <span className="text-[10px] text-[#595959] flex-shrink-0">{task.due_date}</span>
    </div>
  );
}

function TaskSection({ icon, title, color, tasks, onToggle, emptyText }) {
  if (!tasks || tasks.length === 0) return null;
  return (
    <div className="bg-white rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${color}`}>
          {icon}
        </div>
        <h3 className="font-semibold text-sm text-[#2C3E30]">{title} ({tasks.length})</h3>
      </div>
      <div className="space-y-1">
        {tasks.map(t => <TaskRow key={t.id} task={t} onToggle={onToggle} />)}
      </div>
    </div>
  );
}

export default function TasksOverview({ token }) {
  const [data, setData] = useState(null);

  const api = useCallback((method, url) =>
    axios({ method, url: `${BACKEND_URL}${url}`, headers: { Authorization: `Bearer ${token}` } }),
  [token]);

  const load = useCallback(async () => {
    try {
      const res = await api('get', '/api/admin/tasks/overview');
      setData(res.data);
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const toggleTask = async (taskId) => {
    try {
      await api('put', `/api/admin/tasks/${taskId}/toggle`);
      load();
    } catch { /* ignore */ }
  };

  if (!data) return <div className="p-8 text-center text-[#595959]">Chargement...</div>;

  const hasContent = data.overdue?.length > 0 || data.today?.length > 0 || data.upcoming?.length > 0;

  return (
    <div className="space-y-4" data-testid="tasks-overview">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] text-center">
          <p className="text-2xl font-semibold text-[#993333]" data-testid="overdue-count">{data.overdue?.length || 0}</p>
          <p className="text-xs text-[#595959]">En retard</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] text-center">
          <p className="text-2xl font-semibold text-[#C4A962]" data-testid="today-count">{data.today?.length || 0}</p>
          <p className="text-xs text-[#595959]">Aujourd'hui</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] text-center">
          <p className="text-2xl font-semibold text-blue-600" data-testid="upcoming-count">{data.upcoming?.length || 0}</p>
          <p className="text-xs text-[#595959]">7 prochains jours</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] text-center">
          <p className="text-2xl font-semibold text-[#8A9A5B]" data-testid="done-count">{data.total_done || 0}</p>
          <p className="text-xs text-[#595959]">Terminees</p>
        </div>
      </div>

      {!hasContent && (
        <div className="bg-white rounded-xl p-12 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] text-center">
          <ListChecks className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-[#595959]">Aucune tache en cours</p>
          <p className="text-xs text-[#595959] mt-1">Les taches sont generees automatiquement a la confirmation d'une reservation</p>
        </div>
      )}

      <TaskSection
        icon={<AlertTriangle className="w-4 h-4 text-[#993333]" strokeWidth={1.5} />}
        title="En retard"
        color="bg-red-50"
        tasks={data.overdue}
        onToggle={toggleTask}
      />

      <TaskSection
        icon={<Clock className="w-4 h-4 text-[#C4A962]" strokeWidth={1.5} />}
        title="Aujourd'hui"
        color="bg-amber-50"
        tasks={data.today}
        onToggle={toggleTask}
      />

      <TaskSection
        icon={<CalendarDays className="w-4 h-4 text-blue-600" strokeWidth={1.5} />}
        title="7 prochains jours"
        color="bg-blue-50"
        tasks={data.upcoming}
        onToggle={toggleTask}
      />
    </div>
  );
}
